/* ─────────────────────────────────────────────
   PeopleSuiteSidebar.ts
   Collapsible + drag-to-resize sidebar – vanilla TypeScript
───────────────────────────────────────────── */

import { matchesAnyDepartment } from '@hrms/shared';
import { ADMIN_ROLES, isAdminRole, isExecutiveRole, isSuperAdminRole, SUPER_ADMIN_ROLES } from '../../utils/roleHelpers';
import { ROLES_CHANGED_EVENT } from '../../utils/roleStorage';

import {
  NAV_CONSOLIDATE_LEAVE_REPORTS,
  NAV_LEAVE_APPROVALS,
  NAV_LEAVE_MANAGEMENT,
  NAV_LEAVE_POLICIES,
  NAV_LEAVE_REPORTS,
  NAV_ONBOARDING,
  NAV_ONBOARDING_REPORTS,
  NAV_TIMESHEET,
  NAV_ADMIN_TIMESHEET,
  NAV_TIMESHEET_APPROVALS,
  NAV_PRE_ONBOARDING,
  NAV_PRE_ONBOARDING_REPORTS,
} from '../../constants';

import {
  COLLAPSED_WIDTH,
  CSS_VAR,
  DOM_ID,
  EVENT_NAV_CLICK,
  EVENT_SIDEBAR_TOGGLE,
  EXPANDED_WIDTH,
  HANDLE_WIDTH,
  NAV_ITEM_ANIMATION_BASE_DELAY_S,
  NAV_ITEM_ANIMATION_DURATION_S,
  NAV_ITEM_ANIMATION_STEP_S,
  NAV_SECTIONS,
  NavItemDef,
  NavSectionDef,
  RESIZE_COLLAPSE_SNAP_THRESHOLD,
  RESIZE_MAX_WIDTH,
  SELECTOR,
  TOAST_DISPLAY_DURATION_MS,
  TOAST_EXIT_ANIMATION_MS,
  TOAST_ICON_DEFAULT,
  TOAST_ICON_NAVIGATE,
  TOAST_ICON_RESIZE,
  TOAST_MSG_SIDEBAR_RESIZED,
  TOOLTIP_HORIZONTAL_GAP_PX,
  TOOLTIP_VERTICAL_CENTER_OFFSET_PX,
} from './PeopleSuiteSidebar.constants';
import './PeopleSuiteSidebar.scss';

const SIDEBAR_COLLAPSED_KEY = 'ps-sidebar-collapsed';
const SIDEBAR_SCROLL_KEY = 'ps-sidebar-scroll';
const SIDEBAR_ACCORDIONS_KEY = 'ps-sidebar-accordions';

function getStoredCollapsed(): boolean {
  return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
}

function setStoredCollapsed(value: boolean): void {
  localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(value));
}

function getStoredScroll(): number {
  const val = sessionStorage.getItem(SIDEBAR_SCROLL_KEY);
  return val ? Number(val) : 0;
}

function setStoredScroll(value: number): void {
  sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(value));
}

function getStoredOpenSection(): string | null {
  try {
    const val = localStorage.getItem(SIDEBAR_ACCORDIONS_KEY);
    if (!val) return null;
    if (val.startsWith('{')) {
      const parsed = JSON.parse(val);
      return Object.keys(parsed).find((k) => parsed[k] === true) || null;
    }
    return val;
  } catch {
    return null;
  }
}

function setStoredOpenSection(header: string | null): void {
  try {
    if (header) {
      localStorage.setItem(SIDEBAR_ACCORDIONS_KEY, header);
    } else {
      localStorage.removeItem(SIDEBAR_ACCORDIONS_KEY);
    }
  } catch {
    // Ignore storage errors
  }
}

function toggleSection(header: string, groupEl: HTMLElement, forceOpen?: boolean): void {
  const itemsContainer = groupEl.querySelector<HTMLElement>('.ps-nav-section-items');
  const headerEl = groupEl.querySelector<HTMLElement>('.ps-nav-section');
  if (!itemsContainer) return;

  const isCurrentlyCollapsed = itemsContainer.classList.contains('collapsed');
  const willBeOpen = forceOpen !== undefined ? forceOpen : isCurrentlyCollapsed;

  if (willBeOpen) {
    // Single-open accordion mode: Close all other sections
    if (sidebarEl) {
      const allGroups = sidebarEl.querySelectorAll<HTMLElement>('.ps-nav-section-group');
      allGroups.forEach((otherGroup) => {
        if (otherGroup !== groupEl) {
          const otherItems = otherGroup.querySelector<HTMLElement>('.ps-nav-section-items');
          const otherHeaderEl = otherGroup.querySelector<HTMLElement>('.ps-nav-section');
          otherItems?.classList.add('collapsed');
          otherHeaderEl?.classList.add('collapsed');
        }
      });
    }

    itemsContainer.classList.remove('collapsed');
    headerEl?.classList.remove('collapsed');
    setStoredOpenSection(header);
  } else {
    itemsContainer.classList.add('collapsed');
    headerEl?.classList.add('collapsed');
    setStoredOpenSection(null);
  }
}

export interface SidebarOptions {
  onNavigate?: (path: string) => void;
  onToast?: (msg: string, icon: string) => void;
  initialPath?: string;
  role?: string;
  department?: string;
}

/* ── Module state ── */
let isResizing: boolean = false;
let startX: number = 0;
let startWidth: number = 0;
let collapsed: boolean = false;
let _opts: SidebarOptions = {};
let _userRole: string = '';
let _userDepartment: string = '';
let _isGroupOwner: boolean = false;
let _isApprover: boolean = false;

/* ── Typed DOM refs ── */
let sidebarEl!: HTMLElement;
let wrapperEl!: HTMLDivElement;
let tooltipEl!: HTMLDivElement;

/* ── Helpers ── */

function applyCssVariables(): void {
  const root = document.documentElement.style;
  root.setProperty(CSS_VAR.expandedWidth, `${EXPANDED_WIDTH}px`);
  root.setProperty(CSS_VAR.collapsedWidth, `${COLLAPSED_WIDTH}px`);
  root.setProperty(CSS_VAR.handleWidth, `${HANDLE_WIDTH}px`);
}

function updateMainMargin(sidebarWidth: number): void {
  const main = document.querySelector<HTMLElement>(SELECTOR.mainContent);
  if (main) main.style.marginLeft = sidebarWidth + HANDLE_WIDTH + 'px';
}

function getAllNavItems(): NodeListOf<HTMLElement> {
  return sidebarEl.querySelectorAll<HTMLElement>(SELECTOR.navItem);
}

function showToast(msg: string, icon: string = TOAST_ICON_DEFAULT): void {
  const container = document.querySelector<HTMLElement>(SELECTOR.toastContainer);
  if (!container) {
    if (_opts.onToast) _opts.onToast(msg, icon);
    return;
  }
  const toast: HTMLDivElement = document.createElement('div');
  toast.className = 'ps-toast';
  toast.innerHTML = `<span class="material-symbols-outlined ps-toast-icon">${icon}</span><span>${msg}</span>`;
  container.appendChild(toast);
  requestAnimationFrame((): void => {
    requestAnimationFrame((): void => toast.classList.add('show'));
  });
  setTimeout((): void => {
    toast.classList.remove('show');
    setTimeout((): void => toast.remove(), TOAST_EXIT_ANIMATION_MS);
  }, TOAST_DISPLAY_DURATION_MS);
}

function setActiveNav(el: HTMLElement, name: string): void {
  getAllNavItems().forEach((item: HTMLElement): void => item.classList.remove('active'));
  el.classList.add('active');
  const breadcrumb = document.querySelector<HTMLSpanElement>(SELECTOR.breadcrumbActive);
  if (breadcrumb) breadcrumb.textContent = name;
  const path: string = el.getAttribute('data-path') || '/dashboard';
  if (_opts.onNavigate) _opts.onNavigate(path);
  window.dispatchEvent(new CustomEvent(EVENT_NAV_CLICK));
  showToast(`Navigating to ${name}`, TOAST_ICON_NAVIGATE);
}

function toggleSidebar(): void {
  collapsed = !collapsed;
  setStoredCollapsed(collapsed);
  if (collapsed) {
    sidebarEl.style.width = COLLAPSED_WIDTH + 'px';
    sidebarEl.classList.add('collapsed');
    updateMainMargin(COLLAPSED_WIDTH);
  } else {
    sidebarEl.style.width = EXPANDED_WIDTH + 'px';
    sidebarEl.classList.remove('collapsed');
    updateMainMargin(EXPANDED_WIDTH);
  }
}

function showTooltip(label: string, anchorEl: HTMLElement): void {
  tooltipEl.textContent = label;
  const rect: DOMRect = anchorEl.getBoundingClientRect();
  const wrapRect: DOMRect = wrapperEl.getBoundingClientRect();
  tooltipEl.style.top = rect.top + rect.height / 2 - TOOLTIP_VERTICAL_CENTER_OFFSET_PX + 'px';
  tooltipEl.style.left = wrapRect.right + TOOLTIP_HORIZONTAL_GAP_PX + 'px';
  tooltipEl.classList.add('visible');
}

function hideTooltip(): void {
  tooltipEl.classList.remove('visible');
}

/* ── Export: update active nav on route change ── */
export function setActiveNavByPath(path: string): void {
  if (!sidebarEl) return;
  const items = getAllNavItems();

  // Find the most-specific match (longest itemPath that is a prefix of path)
  let bestEl: HTMLElement | null = null;
  let bestLen = 0;
  for (const item of items) {
    const itemPath: string = item.getAttribute('data-path') || '';
    if (!itemPath) continue;
    const exactMatch = path === itemPath;
    const prefixMatch = itemPath.length > 1 && path.startsWith(itemPath + '/');
    if ((exactMatch || prefixMatch) && itemPath.length > bestLen) {
      bestLen = itemPath.length;
      bestEl = item;
    }
  }

  for (const item of items) {
    if (item === bestEl) {
      item.classList.add('active');
    } else {
      item.classList.remove('active');
    }
  }

  // Update breadcrumb with the nav label (e.g. "User Management" not "Users")
  if (bestEl) {
    const label = bestEl.getAttribute('data-label') || '';
    const breadcrumb = document.querySelector<HTMLSpanElement>(SELECTOR.breadcrumbActive);
    if (breadcrumb && label) breadcrumb.textContent = label;

    // Automatically expand the accordion section containing the active item
    const group = bestEl.closest<HTMLElement>('.ps-nav-section-group');
    if (group) {
      const header = group.getAttribute('data-header') || '';
      toggleSection(header, group, true);
    }
  }
}

/* ── Create individual NavItem element ── */
function createNavItem(item: NavItemDef): HTMLDivElement {
  const navItem: HTMLDivElement = document.createElement('div');
  navItem.className = 'ps-nav-item' + (item.multiLine ? ' ps-multiline' : '');
  if (item.path === '/dashboard') navItem.classList.add('ps-dashboard-item');
  navItem.setAttribute('data-path', item.path);
  navItem.setAttribute('data-label', item.label);
  const testIdSlug = item.path.replace(/^\//, '').replace(/\//g, '-') || 'dashboard';
  navItem.setAttribute('data-testid', `nav-item-${testIdSlug}`);

  const iconEl: HTMLSpanElement = document.createElement('span');
  iconEl.className = 'material-symbols-outlined ps-nav-icon';
  iconEl.textContent = item.icon;

  const labelEl: HTMLSpanElement = document.createElement('span');
  labelEl.className = 'ps-nav-label';
  labelEl.textContent = item.label;

  navItem.appendChild(iconEl);
  navItem.appendChild(labelEl);

  navItem.addEventListener('click', (_e: MouseEvent): void => {
    setActiveNav(navItem, item.label);
  });

  navItem.addEventListener('mouseenter', (_e: MouseEvent): void => {
    if (collapsed) showTooltip(item.label, navItem);
  });

  navItem.addEventListener('mouseleave', (_e: MouseEvent): void => {
    hideTooltip();
  });

  return navItem;
}

/* ── Build nav DOM ── */
function buildNavContent(container: HTMLElement, _role?: string): void {
  // Clone NAV_SECTIONS and dynamically inject leave items from constants.ts
  const navSections = NAV_SECTIONS.map((section) => {
    const newSection = { ...section, items: [...section.items] };
    if (newSection.header === 'COMPANY ADMINISTRATION') {
      newSection.items.push(NAV_LEAVE_POLICIES);
    } else if (newSection.header === 'EMPLOYEE LIFECYCLE') {
      newSection.items.push(NAV_PRE_ONBOARDING);
      newSection.items.push(NAV_ONBOARDING);
    } else if (newSection.header === 'EMPLOYEE SERVICES') {
      newSection.items.push(NAV_LEAVE_MANAGEMENT);
      newSection.items.push(NAV_TIMESHEET);
    } else if (newSection.header === 'TEAM MANAGEMENT') {
      newSection.items.push(NAV_LEAVE_APPROVALS);
      newSection.items.push(NAV_TIMESHEET_APPROVALS);
    } else if (newSection.header === 'REPORTS & ANALYTICS') {
      newSection.items.unshift(NAV_ADMIN_TIMESHEET);
      newSection.items.unshift(NAV_CONSOLIDATE_LEAVE_REPORTS);
      newSection.items.unshift(NAV_LEAVE_REPORTS);
      newSection.items.unshift(NAV_ONBOARDING_REPORTS);
      newSection.items.unshift(NAV_PRE_ONBOARDING_REPORTS);
    }
    return newSection;
  });

  // Determine which single section should be open (mutually exclusive)
  const currentPath = window.location.pathname;
  let singleOpenHeader: string | null = null;
  for (const section of navSections) {
    if (!section.header) continue;
    const hasActive = section.items.some((item) => {
      if (!item.path) return false;
      return currentPath === item.path || (item.path.length > 1 && currentPath.startsWith(item.path + '/'));
    });
    if (hasActive) {
      singleOpenHeader = section.header;
      break;
    }
  }

  if (!singleOpenHeader) {
    const stored = getStoredOpenSection();
    singleOpenHeader = stored || 'COMPANY ADMINISTRATION';
  }

  navSections.forEach((section: NavSectionDef): void => {
    const effectiveRole = _role || _userRole;
    const visibleItems = section.items.filter((item: NavItemDef) => {
      // For executive roles, Leave Management is not needed
      const isExec =
        isExecutiveRole(effectiveRole) ||
        ['ceo', 'coo', 'cfo', 'director', 'cmm', 'cto'].includes((effectiveRole || '').toLowerCase().trim());
      if (isExec && (item.path === '/attendance/leaves' || item.label === 'Leave Management')) {
        return false;
      }

      if (item.roles) {
        const hasAdminRoleInItem = item.roles.some((r) => ADMIN_ROLES.includes(r as any));
        const hasSuperAdminRoleInItem = item.roles.some((r) => SUPER_ADMIN_ROLES.includes(r as any));
        const roleOk =
          !!effectiveRole &&
          (item.roles.includes(effectiveRole) ||
            (isAdminRole(effectiveRole) && hasAdminRoleInItem) ||
            (isSuperAdminRole(effectiveRole) && hasSuperAdminRoleInItem));
        const deptOk =
          !!item.departments &&
          effectiveRole === 'employee' &&
          matchesAnyDepartment(_userDepartment, item.departments);
        if (!roleOk && !deptOk) return false;
      }
      if (item.groupOwnerOnly && !_isGroupOwner) return false;
      
      const hasGroupOwnerOrRoles = !!item.groupOwnerOrRoles;
      if (hasGroupOwnerOrRoles) {
        const hasAdminInGroupOwner = item.groupOwnerOrRoles!.some((r) => ADMIN_ROLES.includes(r as any));
        const hasSuperAdminInGroupOwner = item.groupOwnerOrRoles!.some((r) => SUPER_ADMIN_ROLES.includes(r as any));
        const roleMatches =
          !!effectiveRole &&
          (item.groupOwnerOrRoles!.includes(effectiveRole) ||
            (isAdminRole(effectiveRole) && hasAdminInGroupOwner) ||
            (isSuperAdminRole(effectiveRole) && hasSuperAdminInGroupOwner));
        const isApproverItem = item.path.includes('/approvals') || 
                               item.path.includes('/reports/leaves') || 
                               item.path.includes('/reports/consolidate');
        
        // If it's an approvals or reports tab, allow access if they are an approver (HR, manager, etc.) OR if they match regular groupOwner/Admin rules
        if (isApproverItem) {
          if (!_isGroupOwner && !_isApprover && !roleMatches) return false;
        } else {
          // Regular groupOwnerOrRoles check
          if (!_isGroupOwner && !roleMatches) return false;
        }
      }
      return true;
    });
    if (visibleItems.length === 0) return;

    if (section.header) {
      const groupEl: HTMLDivElement = document.createElement('div');
      groupEl.className = 'ps-nav-section-group';
      groupEl.setAttribute('data-header', section.header);

      const headerEl: HTMLDivElement = document.createElement('div');
      headerEl.className = 'ps-nav-section';
      headerEl.setAttribute('role', 'button');
      headerEl.setAttribute('tabindex', '0');
      headerEl.setAttribute('data-header', section.header);
      headerEl.setAttribute('aria-label', `Toggle ${section.header} section`);
      headerEl.setAttribute('data-testid', `nav-section-${section.header.toLowerCase().replace(/\s+/g, '-')}`);

      const titleEl: HTMLSpanElement = document.createElement('span');
      titleEl.className = 'ps-nav-section-title';
      titleEl.textContent = section.header;

      const arrowEl: HTMLSpanElement = document.createElement('span');
      arrowEl.className = 'material-symbols-outlined ps-nav-section-arrow';
      arrowEl.textContent = 'expand_more';

      headerEl.appendChild(titleEl);
      headerEl.appendChild(arrowEl);
      groupEl.appendChild(headerEl);

      const itemsWrap: HTMLDivElement = document.createElement('div');
      itemsWrap.className = 'ps-nav-section-items';
      itemsWrap.setAttribute('data-header', section.header);

      const itemsInner: HTMLDivElement = document.createElement('div');
      itemsInner.className = 'ps-nav-section-items-inner';
      itemsWrap.appendChild(itemsInner);

      const isOpen = section.header === singleOpenHeader;
      if (!isOpen) {
        itemsWrap.classList.add('collapsed');
        headerEl.classList.add('collapsed');
      }

      headerEl.addEventListener('click', (e: MouseEvent): void => {
        e.stopPropagation();
        toggleSection(section.header!, groupEl);
      });

      headerEl.addEventListener('keydown', (e: KeyboardEvent): void => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleSection(section.header!, groupEl);
        }
      });

      visibleItems.forEach((item: NavItemDef): void => {
        const navItem = createNavItem(item);
        itemsInner.appendChild(navItem);
      });

      groupEl.appendChild(itemsWrap);
      container.appendChild(groupEl);
    } else {
      visibleItems.forEach((item: NavItemDef): void => {
        const navItem = createNavItem(item);
        container.appendChild(navItem);
      });
    }
  });

  const scrollIndicator: HTMLDivElement = document.createElement('div');
  scrollIndicator.className = 'ps-scroll-indicator';
  const arrowIcon: HTMLSpanElement = document.createElement('span');
  arrowIcon.className = 'material-symbols-outlined';
  arrowIcon.textContent = 'keyboard_arrow_down';
  scrollIndicator.appendChild(arrowIcon);
  container.appendChild(scrollIndicator);
}

/* ── Update user role and department after auth load/update ── */
export function updateSidebarUser(role?: string, department?: string): void {
  const newRole = role ?? '';
  const newDept = department ?? '';
  if (_userRole === newRole && _userDepartment === newDept) return;
  _userRole = newRole;
  _userDepartment = newDept;
  if (!sidebarEl) return;
  const inner = sidebarEl.querySelector<HTMLElement>('.ps-sidebar-inner');
  if (!inner) return;
  inner.innerHTML = '';
  buildNavContent(inner, _userRole);
  const currentPath = window.location.pathname;
  setActiveNavByPath(currentPath);
}

/* ── Update group owner visibility after async detection ── */
export function updateSidebarGroupOwner(isOwner: boolean): void {
  if (_isGroupOwner === isOwner) return;
  _isGroupOwner = isOwner;
  if (!sidebarEl) return;
  const inner = sidebarEl.querySelector<HTMLElement>('.ps-sidebar-inner');
  if (!inner) return;
  inner.innerHTML = '';
  buildNavContent(inner, _userRole);
  const currentPath = window.location.pathname;
  setActiveNavByPath(currentPath);
}

/* ── Update approver visibility after async detection ── */
export function updateSidebarApprover(isApprover: boolean): void {
  if (_isApprover === isApprover) return;
  _isApprover = isApprover;
  if (!sidebarEl) return;
  const inner = sidebarEl.querySelector<HTMLElement>('.ps-sidebar-inner');
  if (!inner) return;
  inner.innerHTML = '';
  buildNavContent(inner, _userRole);
  const currentPath = window.location.pathname;
  setActiveNavByPath(currentPath);
}

/* ── Update user role/department dynamically ── */
export function updateSidebarRole(role?: string, department?: string): void {
  if (role !== undefined) _userRole = role;
  if (department !== undefined) _userDepartment = department;
  if (!sidebarEl) return;
  const inner = sidebarEl.querySelector<HTMLElement>('.ps-sidebar-inner');
  if (!inner) return;
  inner.innerHTML = '';
  buildNavContent(inner, _userRole);
  const currentPath = window.location.pathname;
  setActiveNavByPath(currentPath);
}

/* ── Mount ── */
export function mountSidebar(opts: SidebarOptions = {}): () => void {
  _opts = opts;
  _userRole = opts.role ?? '';
  _userDepartment = opts.department ?? '';
  collapsed = getStoredCollapsed();

  applyCssVariables();

  /* Build wrapper */
  wrapperEl = document.createElement('div');
  wrapperEl.className = 'ps-sidebar-wrapper';
  wrapperEl.id = DOM_ID.wrapper;

  /* Build sidebar panel */
  sidebarEl = document.createElement('div');
  sidebarEl.className = 'ps-sidebar';
  sidebarEl.id = DOM_ID.sidebar;
  sidebarEl.setAttribute('data-testid', 'sidebar');

  const innerEl: HTMLDivElement = document.createElement('div');
  innerEl.className = 'ps-sidebar-inner';
  sidebarEl.appendChild(innerEl);

  buildNavContent(innerEl, opts.role);

  let scrollSaveTimer: ReturnType<typeof setTimeout> | null = null;
  const onInnerScroll = (): void => {
    if (scrollSaveTimer) clearTimeout(scrollSaveTimer);
    scrollSaveTimer = setTimeout(() => setStoredScroll(innerEl.scrollTop), 100);
  };
  innerEl.addEventListener('scroll', onInnerScroll, { passive: true });

  /* Build resize handle */
  const handleEl: HTMLDivElement = document.createElement('div');
  handleEl.className = 'ps-resize-handle';
  handleEl.id = DOM_ID.resizeHandle;
  const lineEl: HTMLDivElement = document.createElement('div');
  lineEl.className = 'ps-resize-line';
  handleEl.appendChild(lineEl);

  wrapperEl.appendChild(sidebarEl);
  wrapperEl.appendChild(handleEl);

  /* Tooltip at body level */
  tooltipEl = document.createElement('div');
  tooltipEl.className = 'ps-tooltip';
  tooltipEl.id = DOM_ID.tooltip;
  document.body.appendChild(tooltipEl);
  if (collapsed) {
    sidebarEl.style.width = COLLAPSED_WIDTH + 'px';
    sidebarEl.classList.add('collapsed');
    updateMainMargin(COLLAPSED_WIDTH);
  } else {
    sidebarEl.style.width = EXPANDED_WIDTH + 'px';
    updateMainMargin(EXPANDED_WIDTH);
  }
  document.body.appendChild(wrapperEl);
  innerEl.scrollTop = getStoredScroll();

  /* Set initial margin and active state */

  if (opts.initialPath) setActiveNavByPath(opts.initialPath);

  /* ── Resize drag ── */
  const onHandleMouseDown = (e: MouseEvent): void => {
    isResizing = true;
    startX = e.clientX;
    startWidth = sidebarEl.offsetWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };
  handleEl.addEventListener('mousedown', onHandleMouseDown);

  const onDocMouseMove = (e: MouseEvent): void => {
    if (!isResizing) return;
    const newWidth: number = Math.max(
      COLLAPSED_WIDTH,
      Math.min(RESIZE_MAX_WIDTH, startWidth + (e.clientX - startX))
    );
    sidebarEl.style.width = newWidth + 'px';
    updateMainMargin(newWidth);
    const nowCollapsed: boolean = newWidth <= COLLAPSED_WIDTH + RESIZE_COLLAPSE_SNAP_THRESHOLD;
    if (nowCollapsed !== collapsed) {
      collapsed = nowCollapsed;
      setStoredCollapsed(collapsed);
      sidebarEl.classList.toggle('collapsed', collapsed);
    }
  };
  document.addEventListener('mousemove', onDocMouseMove);

  const onDocMouseUp = (_e: MouseEvent): void => {
    if (!isResizing) return;
    isResizing = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    showToast(TOAST_MSG_SIDEBAR_RESIZED, TOAST_ICON_RESIZE);
  };
  document.addEventListener('mouseup', onDocMouseUp);

  /* ── Listen for navbar toggle button ── */
  const onToggleEvent = (_e: Event): void => {
    toggleSidebar();
  };
  window.addEventListener(EVENT_SIDEBAR_TOGGLE, onToggleEvent);

  /* ── Listen for roles cache changes ── */
  const onRolesChanged = (): void => {
    if (!sidebarEl) return;
    const inner = sidebarEl.querySelector<HTMLElement>('.ps-sidebar-inner');
    if (!inner) return;
    inner.innerHTML = '';
    buildNavContent(inner, _userRole);
    setActiveNavByPath(window.location.pathname);
  };
  window.addEventListener(ROLES_CHANGED_EVENT, onRolesChanged);

  /* ── Cleanup ── */
  return (): void => {
    wrapperEl.remove();
    tooltipEl.remove();
    innerEl.removeEventListener('scroll', onInnerScroll);
    if (scrollSaveTimer) clearTimeout(scrollSaveTimer);
    document.removeEventListener('mousemove', onDocMouseMove);
    document.removeEventListener('mouseup', onDocMouseUp);
    window.removeEventListener(EVENT_SIDEBAR_TOGGLE, onToggleEvent);
    window.removeEventListener(ROLES_CHANGED_EVENT, onRolesChanged);
  };
}
